import { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { FOCUSRING } from '../focus';
import { activeWorkQualityTab, metMeereizend, type WorkQualityTab } from '../tabs';

/**
 * De tabbalk van Werkkwaliteit (CAP-10, EXPERIENCE.md "Overal"): navigatie, geen tabpatroon.
 *
 * Links in een `<nav>`, bediend met Tab en Enter; het actieve tabblad volgt uit het adres en draagt
 * `aria-current="page"`, zodat een bladwijzer, verversen en "terug" op hetzelfde tabblad landen.
 * Elke link neemt week en werksoort uit het adres mee. Op een smal scherm scrolt de balk binnen
 * zijn eigen focusbare kader, en het actieve tabblad schuift bij het laden in beeld. Of de balk er
 * staat, beslist de aanroeper: met één weergave is er geen balk (UX-DR2).
 */
export function WorkQualityTabs({ tabs }: { tabs: WorkQualityTab[] }) {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const active = activeWorkQualityTab(pathname);
  const actieveLink = useRef<HTMLAnchorElement>(null);

  // Geen vloeiende beweging: dit gebeurt bij het laden, en `auto` volgt ook `prefers-reduced-motion`.
  // Nog een keer zodra de lettertypen er zijn: een later geladen letter maakt de tabbladen breder.
  useEffect(() => {
    const schuif = () => actieveLink.current?.scrollIntoView?.({ behavior: 'auto', block: 'nearest', inline: 'center' });
    schuif();
    void document.fonts?.ready.then(schuif);
  }, [active]);

  return (
    <nav aria-label={t('qa.bediening.tabbalk.label')} className="mb-4 min-w-0" data-testid="werkkwaliteit-tabs">
      <div
        aria-label={t('qa.bediening.tabbalk.scrollkader')}
        className={`overflow-x-auto rounded-[var(--radius-control)] ${FOCUSRING}`}
        role="region"
        tabIndex={0}
      >
        <ul className="inline-flex items-center gap-1 rounded-[var(--radius-control)] bg-[var(--background-muted)] p-1">
          {tabs.map((tab) => {
            const isActief = tab.id === active;
            return (
              <li key={tab.id}>
                <Link
                  aria-current={isActief ? 'page' : undefined}
                  className={`inline-flex items-center justify-center whitespace-nowrap rounded-[var(--radius-control)] px-3 py-1 text-sm font-medium max-md:min-h-11 ${
                    isActief ? 'bg-[var(--background)] text-[var(--foreground)] shadow-sm' : 'text-[var(--foreground-muted)] hover:text-[var(--foreground)]'
                  } ${FOCUSRING}`}
                  data-testid={tab.testId}
                  ref={isActief ? actieveLink : undefined}
                  to={metMeereizend(tab.to, search)}
                >
                  {t(tab.labelKey)}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
